import { useCallback } from 'react';
import classnames from 'classnames';

import responsePhotoButtonClick from '@hh.ru/analytics-js-events/build/xhh/employer/responses/resume_card/response_photo_button_click';
import { Avatar, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { useVacancyId } from 'src/components/ResumeCard/hooks/useVacancyId';
import translation from 'src/components/translation';
import { useOnOffState } from 'src/hooks/useOnOffState';
import { Gender } from 'src/models/resume/resumeCommon.types';
import { PhotoUrls } from 'src/models/resumeCard';

import PhotoModal from 'src/components/ResumeCard/Header/Photo/Modal';
import VideoLink from 'src/components/ResumeCard/Header/Photo/VideoLink';

import styles from './styles.less';

const TrlKeys = {
    [Gender.Female]: 'resume.photo.gender.female',
    [Gender.Male]: 'resume.photo.gender.male',
    [Gender.Unknown]: 'resume.photo.gender.unknown',
};

interface Props {
    /** ФИО соискателя */
    fullName: string | null;

    /** Гендер */
    gender: Gender;

    /** Идентификатор резюме */
    resumeId: string;

    /** Идентификатор топика */
    topicId?: string;

    /** Объект фотографии из резюме */
    userPhotoUrls?: PhotoUrls;

    /** Ссылка на видео самопрезентации */
    videoLink?: string;

    /** Соискатель сейчас на сайте */
    isOnline?: boolean;
}

const Photo: TranslatedComponent<Props> = ({
    fullName,
    gender,
    resumeId,
    topicId,
    userPhotoUrls,
    isOnline,
    videoLink,
    trls,
}) => {
    const { isGtS } = useBreakpoint();

    const [isVisibleModal, openModal, closeModal] = useOnOffState(false);
    const vacancyId = useVacancyId();
    const handleClick = useCallback(() => {
        if (!userPhotoUrls) {
            return;
        }
        openModal();
        if (vacancyId && topicId) {
            responsePhotoButtonClick({ vacancyId: vacancyId.toString(), topicId });
        }
    }, [openModal, topicId, userPhotoUrls, vacancyId]);

    const altText = trls[TrlKeys[gender]];

    return (
        <>
            <div className={classnames(styles.wrapper, 'noprint')}>
                <Avatar
                    Element={userPhotoUrls ? 'button' : 'div'}
                    size={isGtS ? 96 : 48}
                    mode={userPhotoUrls?.big ? 'image' : 'placeholder'}
                    image={userPhotoUrls?.big}
                    fallbackMode="placeholder"
                    placeholder={gender === Gender.Unknown ? 'undefined' : gender}
                    online={isOnline}
                    aria-label={altText}
                    onClick={handleClick}
                    data-interactive
                    containerDataQa={classnames('resume-card-avatar', {
                        'resume-card-avatar_with-user-photo': userPhotoUrls,
                        'resume-card-avatar_with-placeholder': !userPhotoUrls,
                        'resume-card-avatar_with-online': isOnline,
                    })}
                />
                {userPhotoUrls && (
                    <PhotoModal
                        isVisible={isVisibleModal}
                        close={closeModal}
                        fullName={fullName}
                        imgUrl={userPhotoUrls.big}
                        altText={altText}
                    />
                )}
                {isGtS && videoLink && <VideoLink videoLink={videoLink} resumeId={resumeId} />}
            </div>
            {!isGtS && videoLink && (
                <>
                    <VSpacing default={24} />
                    <VideoLink videoLink={videoLink} resumeId={resumeId} isMobile />
                </>
            )}
        </>
    );
};

export default translation(Photo);
